import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import { TenantTranslateCompiler } from './core/i18n/tenant-translate-compiler';
import { CustomTranslateLoader } from './core/i18n/custom-translate-loader';
import { ColorPickerModule } from 'ngx-color-picker';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { ToastrModule } from 'ngx-toastr';

import { routes } from './app.routes';
import { ApplicationService, AuthService, ThemeService } from './core/services';
import { authInterceptor } from './core/interceptors';
import { dateInterceptor } from './core/interceptors/date-interceptor';

function initFactory(authService: AuthService, applicationService: ApplicationService, themeService: ThemeService) {
  return async () => {
    await authService.init();
    await applicationService.init();
    await themeService.init();
  };
}

export function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: 'placeholder',
    },
  });
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    // provideRouter(routes, withDebugTracing()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor]), withInterceptors([dateInterceptor])),
    provideAnimations(),
    TranslateService,
    ColorPickerModule,
    provideCharts(withDefaultRegisterables()),
    TranslateStore,
    MsalModule,
    MsalService,
    {
      provide: APP_INITIALIZER,
      useFactory: initFactory,
      deps: [AuthService, ThemeService, ApplicationService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient) => new CustomTranslateLoader(http, './assets/i18n/', '.json'),
          deps: [HttpClient],
        },
        compiler: { provide: TranslateCompiler, useClass: TenantTranslateCompiler },
      }),
      ToastrModule.forRoot({
        timeOut: 3000,
        progressBar: true,
      }),
    ),
  ],
};
